/**
 * Work Orders — Phase 2 of the procurement overhaul.
 * The WO is the service-side equivalent of a PO: measured works, lump sum,
 * supply+erection, consultancy, equipment hire and AMC contracts.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileSignature, Plus, Search, Loader2, TrendingUp } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import WorkOrderCreate from './WorkOrderCreate';
import { useUrlTab } from '../../lib/hooks/useUrlState';

export const CONTRACT_TYPES = [
  { id: 'measured_works', label: 'Measured Works', desc: 'Item-rate works, billed via measurement book' },
  { id: 'lump_sum', label: 'Lump Sum', desc: 'Milestone-based fixed price' },
  { id: 'supply_erection', label: 'Supply + Erection', desc: 'Hybrid material + works' },
  { id: 'consultancy', label: 'Consultancy', desc: 'Professional service, deliverable-based' },
  { id: 'equipment_hire', label: 'Equipment Hire', desc: 'Hourly / daily hire with logs' },
  { id: 'amc_recurring', label: 'AMC / Recurring', desc: 'Monthly service with SLA' },
];

export const WO_STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-50 text-amber-700',
  approved: 'bg-blue-50 text-blue-700',
  active: 'bg-emerald-50 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-800',
  terminated: 'bg-red-50 text-red-700',
  closed: 'bg-slate-200 text-slate-700',
};

export const ctLabel = (id) => CONTRACT_TYPES.find((c) => c.id === id)?.label || id;

export default function WorkOrders() {
  const nav = useNavigate();
  const [tab, setTab] = useUrlTab('tab', '');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  // Phase 8 — committed cost KPI
  const [committed, setCommitted] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (tab) params.contract_type = tab;
      if (q) params.q = q;
      const d = await apiGet('/work-orders', params);
      setItems(d?.items || []);
      setTotal(d?.total || 0);
      setPages(d?.pages || 1);
    } catch { toast.error('Failed to load work orders'); }
    finally { setLoading(false); }
  }, [tab, page, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { setPage(1); }, [tab, q]);
  // Phase 8 — committed cost summary (all projects)
  useEffect(() => {
    apiGet('/work-orders/committed-costs').then(setCommitted).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="work-orders-page">
      {/* Phase 8 committed-cost KPI strip */}
      {committed && committed.total_committed > 0 && (
        <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-sm px-5 py-3 text-xs" data-testid="committed-cost-kpi">
          <TrendingUp size={15} className="text-[#3b4872] shrink-0" />
          <div>
            <span className="text-slate-500 font-semibold">Total Committed (all open WOs):</span>
            {' '}
            <span className="font-display font-extrabold text-[#3b4872] text-sm">{formatINR(committed.total_committed)}</span>
          </div>
          {Object.entries(committed.breakdown_by_category || {}).map(([k, v]) => (
            <div key={k} className="text-slate-400 hidden sm:block">
              {k.replace(/_/g, ' ')}: <span className="text-slate-600 font-bold">{formatINR(v)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-display">
            <FileSignature size={11} /> Procurement · Service Contracts
          </div>
          <h1 className="font-display font-bold text-2xl text-slate-900 mt-1 flex items-center gap-2">
            <FileSignature size={22} className="text-[#3b4872]" /> Work Orders
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Service-side contracts: works, consultancy, hire &amp; AMC — with milestones, retention, VOs and the full approval matrix.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm font-display font-bold uppercase tracking-wider"
          data-testid="wo-new-btn"
        >
          <Plus size={12} /> New Work Order
        </button>
      </div>

      {/* Type tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {[{ id: '', label: 'All' }, ...CONTRACT_TYPES].map((c) => (
          <button key={c.id || 'all'} onClick={() => setTab(c.id)}
            className={`px-3 py-2 text-xs font-display font-bold uppercase tracking-wide whitespace-nowrap border-b-2 -mb-px ${tab === c.id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`wo-tab-${c.id || 'all'}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search WO no, title, vendor…"
          className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:border-[#3b4872]"
          data-testid="wo-search-input" />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : (
          <table className="w-full dense-table" data-testid="wo-table">
            <thead>
              <tr><th>WO No</th><th>Title</th><th>Vendor</th><th>Project</th><th>Type</th><th className="text-right">Value</th><th className="text-right">Certified %</th><th>Status</th><th>Created</th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No work orders</td></tr>}
              {items.map((w) => {
                const certPct = w.contract_value ? Math.round((w.certified_to_date || 0) / w.contract_value * 100) : 0;
                return (
                  <tr key={w.id} className="cursor-pointer" onClick={() => nav(`/app/procurement/work-orders/${w.id}`)} data-testid={`wo-row-${w.wo_no}`}>
                    <td className="font-mono text-xs text-[#3b4872] font-bold">{w.wo_no}</td>
                    <td className="text-xs max-w-[220px] truncate font-display font-semibold">{w.title}</td>
                    <td className="text-xs">{w.vendor_name || '—'}</td>
                    <td className="text-xs">{w.project_name || '—'}</td>
                    <td className="text-xs">{ctLabel(w.contract_type)}</td>
                    <td className="text-right tabular-nums text-xs font-display font-bold">{formatINR(w.contract_value)}</td>
                    <td className="text-right tabular-nums text-xs">{certPct}%</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${WO_STATUS_BADGE[w.status] || ''}`}>{(w.status || '').replace('_', ' ')}</span></td>
                    <td className="text-xs text-slate-500">{formatDate(w.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-600">
          <span>Page {page} of {pages} · {total} work orders</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2.5 py-1 border border-slate-200 rounded-sm disabled:opacity-40 bg-white">Prev</button>
            <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-2.5 py-1 border border-slate-200 rounded-sm disabled:opacity-40 bg-white">Next</button>
          </div>
        </div>
      )}

      {createOpen && <WorkOrderCreate onClose={(saved) => { setCreateOpen(false); if (saved) load(); }} />}
    </div>
  );
}
